import React, { useEffect, useState, useRef } from "react"
import { useSelector } from "react-redux"
import PropTypes from "prop-types"
import css from "../Auth.scss"
import InputField from "@components/nonDLSComponents/InputField/InputField.js"
import ConditionalLink from "@wrappers/ConditionalLink/ConditionalLink"
import { directAxiosInstance } from "@lib/api"
import Button from "@components/Button/Button"
import { getAssetPath, Validate } from "@commonUtils"
import { clearUserInfo, setAuthType, updateAuthenticationInfo, setDefault } from "../reducer"
import { onGetVerificationCodeGA } from "../GAEvents"
import { setDefaultAuthConfig } from "@containers/App/reducer"
import { useDispatch } from "react-redux"
import ARIAButton from "@wrappers/ARIAButton/ARIAButton"

const Login = ({ forwardHandler, state, localDispatch }) => {
    const { authConfig } = useSelector((state) => state.shellReducer)
    const { info } = state
    const dispatch = useDispatch()
    const [phone, setPhone] = useState(info.phone || "")
    const [loginType, setLoginType] = useState(info.email ? "email" : "phone")
    const [error, setError] = useState(null)
    const [ctaLabel, setCtaLabel] = useState("Send OTP")
    const [email, setEmail] = useState(info.email || "")
    // we are using "active" as a flag so that untill the request is completed, we do not allow multiple request
    // on multiple clicks on cta
    const [active, setActive] = useState(false)
    const [focused, setFocused] = useState(false)
    const [iconUrl, setIconUrl] = useState(null)

    const onFocus = () => {
        setFocused(true)
    }
    /**
     * @description when user come back from otp screen to login screen input should be visible .
     */
    const inputRef = useRef(null)

    const setInputFocus = () => {
        setFocused(true)
        inputRef.current.focus()
    }

    useEffect(() => {
        setInputFocus()
    }, [])

    useEffect(() => {
        if (!error) return

        if (error.phone || error.email) {
            setInputFocus()
        }
    }, [error])

    const onBlur = () => {
        if (email === "" && phone === "") {
            setFocused(false)
        }
    }
    /**
     *
     * @description Function to submit the form by clicking on enter
     */
    const onKeyUp = (event) => {
        if (event.key === "Enter") {
            handleSubmit()
        }
    }
    /**
     * @description Function to check valid input
     */
    const errorCheck = () => {
        const userData = {}

        setError(null)
        const isEmail = loginType === "email"
        if (isEmail) {
            userData.email = email
        } else {
            userData.phone = phone
        }
        const errs = Validate(userData)

        setError(errs)
    }
    /**
     * @description Function to handle user input because input can be email or phone.
     */
    const handleUserInput = (type, value) => {
        const trimmedValue = value.trim()
        const regexpForNumbers = /^\d+$/

        const typeResult = regexpForNumbers.test(trimmedValue) || value === "" ? "phone" : "email"
        if (typeResult === "phone" && email !== "") {
            setEmail("")
        }
        type = typeResult
        setLoginType(typeResult)

        switch (type) {
            case "phone": {
                setPhone(trimmedValue)
                break
            }
            case "email": {
                setEmail(trimmedValue)
                break
            }
        }
    }
    /**
     * @description Function to create otp request for login flow .
     */
    const handleSubmit = () => {
        const errs = errorCheck()
        if (!errs && !active) {
            localDispatch(clearUserInfo())
            setActive(true)
            setIconUrl(getAssetPath("svgs/icons/loading-wheel_24px_background.svg"))

            directAxiosInstance()
                .post(
                    loginType === "phone" ? "auth/create_token" : "auth/create_token_email",

                    loginType === "phone"
                        ? {
                              number: phone,
                          }
                        : {
                              email: email,
                          }
                )
                .then((res) => {
                    if (res && res.data) {
                        if (!res.data.errors) {
                            const { is_success } = res
                            const userFlags = res?.data?.user_flags
                            const existingUser = userFlags?.user_type === "existing"
                            localDispatch(updateAuthenticationInfo({ existingUser: existingUser }))
                            if (is_success) {
                                if (phone) localDispatch(updateAuthenticationInfo({ phone: phone }))
                                else if (email) localDispatch(updateAuthenticationInfo({ email: email }))
                                onGetVerificationCodeGA("Success")
                                forwardHandler("SignInOtp")
                            }
                        }

                        setActive(false)
                        setIconUrl(null)
                        setCtaLabel("Send OTP")
                    }
                })
                .catch((err) => {
                    let errMsg = err?.data?.error?.message || "Server error"
                    if (err?.data?.error?.errors[0]?.message) {
                        errMsg = err.data.error.errors[0].message
                    }
                    setActive(false)
                    setError({ phone: errMsg, email: errMsg })
                    setIconUrl(null)
                    setCtaLabel("Send OTP")
                    onGetVerificationCodeGA("Error")
                })
        }
    }

    return (
        <>
            <div className={css.rightContainer}>
                {!authConfig.restrictUserFromClosingAuth && (
                    <div className={`flex ${css.crossIcon} ${css.cursorPointer}`}>
                        <div>
                            <ARIAButton
                                onClick={() => {
                                    dispatch(setDefaultAuthConfig())
                                    localDispatch(setDefault())
                                }}
                                ariaLabel={"Close login popup"}
                            >
                                <img
                                    src={getAssetPath("svgs/icons/cross_18px_primary.svg")}
                                    alt={"Close icon"}
                                />
                            </ARIAButton>
                        </div>
                    </div>
                )}
                <div className={css.authContainer}>
                    <div className={`${css.paddingAuth} flex flexColumn justifyBetween`}>
                        <div>
                            <span className={css.heading}>Login</span>
                            <div className={`smallRegular textSecondary ${css.subHeadingMargin}`}>
                                Get access to your orders, lab tests & doctor consultations
                            </div>

                            <div className={css.marginBottom16} data-testid="inputField_login">
                                <InputField
                                    name="phone"
                                    label="Enter Your mobile number or email"
                                    type={"text"}
                                    value={loginType === "email" ? email : phone}
                                    onChange={(value) =>
                                        handleUserInput(loginType === "email" ? "email" : "phone", value)
                                    }
                                    onBlur={() => onBlur()}
                                    alertType={"error"}
                                    alertMessage={error && error.phone}
                                    onFocus={() => onFocus()}
                                    focused={focused}
                                    ref={inputRef}
                                    borderType={"primary"}
                                    onKeyUp={(event) => onKeyUp(event)}
                                    required
                                />
                            </div>
                        </div>
                        <div className=" flexColumn alignCenter ">
                            <Button
                                label={!iconUrl ? ctaLabel : null}
                                iconUrl={iconUrl}
                                isDisabled={iconUrl ? true : false}
                                onClick={() => handleSubmit()}
                            />
                            <div className="marginTop-16 flexColumn alignCenter  ">
                                <span className="marginTop-8">
                                    New on 1mg ?
                                    <span
                                        className={`textAction bodyMedium ${css.marginLeft4} ${css.cursorPointer}`}
                                        onClick={() => {
                                            localDispatch(clearUserInfo())
                                            localDispatch(setAuthType("SignUp"))
                                        }}
                                    >
                                        Sign Up
                                    </span>
                                </span>
                                <div className={`${css.footerSubText} flexColumn alignCenter `}>
                                    <div className="textTertiary flexColumn alignCenter ">
                                        By signing up, you agree to our
                                        <div>
                                            <span
                                                className={`textTertiary ${css.underlineText}`}
                                                onClick={() => {
                                                    localDispatch(setDefault())
                                                    window.location.href = "/tnc"
                                                }}
                                            >
                                                Terms and Conditions
                                            </span>
                                            <span className={css.marginBoth4}>&</span>
                                            <span
                                                className={`textTertiary ${css.underlineText}`}
                                                onClick={() => {
                                                    localDispatch(setDefault())
                                                    window.location.href = "/PrivacyPolicy"
                                                }}
                                            >
                                                Privacy policy
                                            </span>
                                        </div>
                                    </div>
                                    <ConditionalLink
                                        className={` ${css.marginFooter} ${css.cursorPointer} smallSemiBold textAction`}
                                        onClick={() => forwardHandler("NeedHelp")}
                                    >
                                        <span>Need Help?</span>
                                        <span className={css.marginLeft4}> Get in touch</span>
                                    </ConditionalLink>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

Login.propTypes = {
    forwardHandler: PropTypes.func.isRequired,
    state: PropTypes.shape({
        info: PropTypes.shape({
            email: PropTypes.string,
            phone: PropTypes.string,
        }),
    }).isRequired,
    localDispatch: PropTypes.func.isRequired,
}

export default Login
